import L from "leaflet";
import {Tooltip} from "bootstrap";
import {v5 as uuidv5} from "uuid";
import constants from "@/libs/Constants";

/* ----
 * I18N
 * ---- */
/**
 * Retrieve percentile label from translations based on value
 * @param percentile Percentile value to retrieve
 * @returns {string}
 */
export function getPercentileI18NLabel(percentile) {
    let label = '';
    switch (percentile) {
        case constants.PERCENTILES.BELOW_MEAN:
            label = 'InputSearchFormLabelValue.PercentileBelowAvg';
            break;
        case constants.PERCENTILES.MEAN:
            label = 'InputSearchFormLabelValue.PercentileOnAverage';
            break;
        case constants.PERCENTILES.ABOVE_MEAN:
            label = 'InputSearchFormLabelValue.PercentileAboveAvg';
            break;
    }
    return label;
}


export function getSeasonI18NLabel(season) {
    return `InputSeasons.${season}`;
}

export function getSeasonTooltipI18NLabel(season) {
    return `InputSeasonsTooltip.${season}`;
}

export function getVisualizationComponentI18NLabel(component) {
    let labelMap = {
        [constants.COMPONENTS.MONITORING]: 'SearchForm.monitoring',
        [constants.COMPONENTS.FORECAST]: 'SearchForm.forecast',
        [constants.COMPONENTS.PROJECTION]: 'SearchForm.projection',
    }
    return labelMap.hasOwnProperty(component) ? labelMap[component] : '';
}

export function getWaterInfrastructureTypeI18NLabel(layerType) {
    let labelMap = {
        [constants.LAYERS.DAM_LAKE]: 'Works.DamLake',
        [constants.LAYERS.DERIVATION]: 'Works.Derivation',
        [constants.LAYERS.PLANT]: 'Works.Plant',
        [constants.LAYERS.RIVER]: 'Works.River',
        [constants.LAYERS.LAKES]: 'Works.Lakes',
        [constants.LAYERS.GLACIER]: 'Works.Glaciers',
        [constants.LAYERS.DERIVATION_POINTS]: 'Works.DerivationPoint',
    }
    return labelMap.hasOwnProperty(layerType) ? labelMap[layerType] : null;
}

/* ---
 * CSS
 * --- */
export function anthropicWorkBadgeColorClass(layerType) {
    let colorMap = {
        [constants.LAYERS.DAM_LAKE]: 'bg-color-anthropic-work-lake',
        [constants.LAYERS.DERIVATION]: 'bg-color-anthropic-work-derivation',
        [constants.LAYERS.DERIVATION_POINTS]: 'bg-color-anthropic-work-derivation',
        [constants.LAYERS.PLANT]: 'bg-color-anthropic-work-plant',
        [constants.LAYERS.RIVER]: 'bg-color-anthropic-work-river',
        [constants.LAYERS.LAKES]: 'bg-color-lake',
        [constants.LAYERS.GLACIER]: 'bg-color-glacier',
    }

    return colorMap.hasOwnProperty(layerType) ? colorMap[layerType] : 'text-bg-secondary';
}

/* --------
 * Tooltips
 * -------- */
/**
 * Initialize Tooltip objects from Bootstrap by class name
 * @param className Class to initialize
 */
export function setupTooltipsByClass(className) {
    const tooltipTriggerList = document.querySelectorAll(className);
    [...tooltipTriggerList].map(tooltipTriggerEl => {
        let tooltipObj = Tooltip.getOrCreateInstance(tooltipTriggerEl);
        if (tooltipObj !== null)
            tooltipObj.dispose();

        new Tooltip(tooltipTriggerEl);
    });
}

/**
 * Remove Tooltip objects from Bootstrap by class name
 * @param className Class to remove objects
 */
export function disposeTooltipsByClass(className) {
    const tooltipTriggerList = document.querySelectorAll(className);
    [...tooltipTriggerList].map(tooltipTriggerEl => {
        let tooltipObj = Tooltip.getOrCreateInstance(tooltipTriggerEl);
        if (tooltipObj !== null)
            tooltipObj.dispose();
    });
}

export function getTooltipsByClass(className) {
    let tooltipList = [];

    const tooltipTriggerList = document.querySelectorAll(className);
    [...tooltipTriggerList].map(tooltipTriggerEl => {
        let tooltipObj = Tooltip.getOrCreateInstance(tooltipTriggerEl);
        if (tooltipObj !== null)
            tooltipList.push(tooltipObj);
    });

    return tooltipList;
}

export function getTooltipById(id) {
    let tooltipInstance = null;
    let myTooltipEl = document.getElementById(id);

    if (myTooltipEl !== null && myTooltipEl !== undefined) {
        tooltipInstance = Tooltip.getOrCreateInstance(myTooltipEl)
    }
    return tooltipInstance;
}

/* -----
 * Dates
 * ----- */
export function monthToStringFormat(month) {
    return month < 10 ? `0${month}` : month;
}

/**
 * Retrieve the first or last day of a year-month string as a date object
 *
 * @param yearMonth Year + month string
 * @param separator Separator to split the year and month in string
 * @param lastDay Use last day instead of first
 * @returns {null} Date object
 */
export function yearMonthToDateFirstOrLastDay(yearMonth, separator = '-', lastDay = false) {
    let dateValue = null;

    let yearMonthItems = yearMonth.split(separator);
    if (yearMonthItems.length === 2) {
        let yearValue = parseInt(yearMonthItems[0]);
        let monthValue = parseInt(yearMonthItems[1]);

        if (isNaN(yearValue) === false && isNaN(monthValue) === false && monthValue >= 1 && monthValue <= 12) {
            if (lastDay === true) {
                dateValue = new Date(yearValue, monthValue, 1);
                dateValue.setDate(dateValue.getDate() - 1);
            } else {
                dateValue = new Date(yearValue, monthValue - 1, 1);
            }
        }
    }

    return dateValue;
}

export function yearMonthToDate(yearMonth, separator = '-') {
    return yearMonthToDateFirstOrLastDay(yearMonth, separator);
}

export function yearMonthToDateLastDay(yearMonth, separator = '-') {
    return yearMonthToDateFirstOrLastDay(yearMonth, separator, true);
}

export function dateObjectToYearMonthStr(date) {
    let monthValue = date.getMonth() + 1;
    let monthValueStr = monthToStringFormat(monthValue);
    return `${date.getFullYear()}-${monthValueStr}`;
}

export function yearMonthToStr(year, month) {
    let dateStr = null;
    if (month !== null && year !== null) {
        let variableMonthDate = new Date(year, month - 1, 1);
        dateStr = dateObjectToYearMonthStr(variableMonthDate);
    }
    return dateStr;
}

/**
 * Based on a list of YYYY-MM items, build the configuration for the VueDatePicker component
 * @param monthYearList List of year-months items (YYYY-MM format)
 * @param useMinDateAsFocus Use minimum date as the focus of the calendar. False by default
 * @returns {{minDate: null, availableDates, minYear: null, maxYear: null, availableYears: *[], maxDate: null, missingDates: *[], focusStartDate: null}}
 */
export function monthYearCalendarProperties(monthYearList, useMinDateAsFocus = false) {
    let minDate = null;
    let maxDate = null;

    let minYear = null;
    let maxYear = null;

    let availableDatesDT = [];

    monthYearList.forEach(yearMonth => {
        //Get minimum and maximum year for ranges
        let monthYearItems = yearMonth.split('-');
        let itemYear = parseInt(monthYearItems[0]);

        if (isNaN(itemYear) === false) {
            if (minYear === null || minYear > itemYear)
                minYear = itemYear;

            if (maxYear === null || maxYear < itemYear)
                maxYear = itemYear;
        }

        /**
         * Note: Vue-datepicker needs the last day of
         * the year-month when month picker is configured
         */
        let monthYearDateDT = yearMonthToDateLastDay(yearMonth);

        //Get minimum and maximum date
        if (monthYearDateDT !== null) {
            if (minDate === null || minDate > monthYearDateDT)
                minDate = monthYearDateDT;

            if (maxDate === null || maxDate < monthYearDateDT)
                maxDate = monthYearDateDT;
        }

        availableDatesDT.push(monthYearDateDT);
    });

    let missingDates = [];
    if (minDate !== null && maxDate !== null) {
        let minDateDT = new Date(minDate.getFullYear(), minDate.getMonth(), 1);

        //Get list of missing dates between minimum and maximum dates
        while (minDateDT <= maxDate) {
            let dateStr = dateObjectToYearMonthStr(minDateDT);
            if (monthYearList.indexOf(dateStr) === -1)
                missingDates.push(yearMonthToDateLastDay(dateStr));

            minDateDT.setMonth(minDateDT.getMonth() + 1);
        }
    }

    return {
        minDate: minDate, //Minimum month available (Date object)
        maxDate: maxDate, //Maximum month available (Date object)
        minYear: minYear, //Minimum year available (Integer)
        maxYear: maxYear, //Maximum year available (Integer)
        focusStartDate: useMinDateAsFocus === true ? minDate : maxDate, //Focus month
        availableDates: availableDatesDT, //List of available months (Date objects)
        availableDatesStr: monthYearList, //List of available months (Strings)
        missingDates: missingDates, //Dates not available in list between minimum and maximum month (Date objects)
        availableYears: [minYear, maxYear], //Interval of years available in calendar
    };
}

export function yearMonthStrToMonthYearI18N(yearMonth, i18n, style = 'short') {
    let retValue = '';

    let yearMonthItems = yearMonth.split('-');
    if (yearMonthItems.length === 2) {
        let yearValue = parseInt(yearMonthItems[0]);
        let monthValue = parseInt(yearMonthItems[1]);

        if (isNaN(yearValue) === false && isNaN(monthValue) === false && monthValue >= 1 && monthValue <= 12)
            retValue = `${monthNumberToLabelI18N(monthValue, i18n, style)}/${yearValue}`;

    }

    return retValue;
}

export function yearMonthStrToMonthI18N(yearMonth, i18n, style = 'short') {
    let retValue = '';

    let yearMonthItems = yearMonth.split('-');
    if (yearMonthItems.length === 2) {
        let monthValue = parseInt(yearMonthItems[1]);
        retValue = monthNumberToLabelI18N(monthValue, i18n, style)
    }

    return retValue;
}

export function monthToNumber(value) {
    let monthValue = Number(value);
    return isNaN(monthValue) === false && monthValue >= 1 && monthValue <= 12 ?
        monthValue :
        null;
}

export function monthNumberToLabelI18N(month, i18n, style = 'short') {
    let label = '';

    let monthNumber = monthToNumber(month);
    if (monthNumber !== false)
        label = i18n.d(
            new Date(
                1,
                monthNumber - 1,
                1
            ),
            {'month': style});

    return label;
}

/* -----
 * Forms
 * ----- */
export function UUIDfromList(items) {
    return uuidv5(items.join(','), uuidv5.URL);
}

export function downloadFileFromAElement(fileName, data) {
    let fileUrl = window.URL.createObjectURL(data);
    let fileLink = document.createElement('a');
    fileLink.href = fileUrl;

    //File name to download (from header)
    fileLink.setAttribute('download', fileName);

    //Simulate click
    fileLink.click();
}

/**
 * Validate e-mail
 * @param email E-mail text
 * @returns {boolean} Whether the e-mail is valid or not
 */
export function isValidEmail(email) {
    return /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(email);
}

/**
 * Return flag to verify registered e-mail on landing page is set to true
 * @returns {boolean}
 */
export function flagRegisteredEmail() {
    return process.env.VUE_APP_CHECK_REGISTERED_EMAIL === 'true';
}

export function flagUsePixel() {
    return process.env.VUE_APP_USE_PIXEL === 'true';
}

export function flagStoreStepToasts() {
    return process.env.VUE_APP_STORE_STEP_TOASTS === 'true';
}

export function flagSimulationMaxAttempts() {
    let maxAttempts = Number(process.env.VUE_APP_SIMULATION_MAX_ATTEMPTS);
    return isNaN(maxAttempts) === false ? maxAttempts : 5;
}

export function flagSimulationCheckTimeout() {
    let checkTimeout = Number(process.env.VUE_APP_SIMULATION_CHECK_TIMEOUT);
    return isNaN(checkTimeout) === false ? checkTimeout : 3000;
}

/**
 * Check undefined value or return default value
 * @param variable
 * @param defaultValue
 * @returns {*}
 */
export function checkUndefinedOrDefaultValue(variable, defaultValue) {
    return variable !== undefined && variable !== null ? variable : defaultValue;
}

export function round(number, digits = 2) {
    let numberN = Number(number);
    return isNaN(numberN) === false ? numberN.toFixed(digits) : number;
}

/* ------
 * Colors
 * ------ */
export function getStyleColorByVariable(colorVariableName) {
    return colorVariableName !== null ?
        getComputedStyle(document.documentElement).getPropertyValue(colorVariableName).trim() :
        '#000000';
}

/**
 * Given a layer type, return the color from a CSS variable
 * @param layerType Layer type
 * @returns {string|string} Color for loaded property or black if not found
 */
export function mapLayersGetColorFromCSSVar(layerType) {
    let mapColor = {
        [constants.LAYERS.DAM_LAKE]: '--anthropic-work-lake-color',
        [constants.LAYERS.PLANT]: '--anthropic-work-plant-color',
        [constants.LAYERS.DERIVATION]: '--anthropic-work-derivation-color',
        [constants.LAYERS.DERIVATION_POINTS]: '--anthropic-work-derivation-color',
        [constants.LAYERS.RIVER]: '--anthropic-work-river-color',
        [constants.LAYERS.MUNICIPALITIES]: '--municipalities-color',
        [constants.LAYERS.GLACIER]: '--anthropic-work-glacier-color',
        [constants.LAYERS.LAKES]: '--anthropic-work-lakes-color',
        [constants.LAYERS.AREA]: '--area-of-interest-color',
        [constants.LAYERS.AGRICULTURE_AREA]: '--anthropic-work-agriculture-area-color',
        [constants.LAYERS.DERIVATION_DIRECTION]: '--anthropic-work-derivation-direction-color',
    }

    return getStyleColorByVariable(mapColor[layerType] || null);
}

export function sortObjectListByNumericKey(items, key, reverse = false) {
    return items.sort((a, b) => {
        let a_key = a[key];
        let b_key = b[key];

        return reverse === true ?
            b_key - a_key :
            a_key - b_key;

    });
}